/**
 * Why a chat answer has no text of its own: the turn ended without one. Jupiter stores
 * the code, and the answer shows it in the reader's language.
 */
export type ChatNotice =
  | "SEARCH_LIMIT"
  | "UNSOURCED"
  | "NO_ANSWER"
  | "TIMEOUT"
  | "SAVE_CONFLICT"
  | "SERVICE_UNAVAILABLE"
  | "UNEXPECTED_ERROR";

// German fallbacks, used when the host app's i18n has no `chatNotice.<code>` key.
export const DEFAULT_CHAT_NOTICES: Record<ChatNotice, string> = {
  SEARCH_LIMIT:
    "Ich habe die Suche wegen des Tiefenlimits abgebrochen und dabei keine belastbare Antwort gefunden. Bitte grenzen Sie die Frage weiter ein.",
  UNSOURCED:
    "Ich konnte keine Antwort finden, die sich mit den gefundenen Quellen belegen lässt. Bitte grenzen Sie die Frage weiter ein.",
  NO_ANSWER: "❌ Ich konnte leider keine Antwort generieren. Bitte versuchen Sie es erneut.",
  TIMEOUT: "⏱️ Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.",
  SAVE_CONFLICT:
    "🔄 Beim Speichern der Antwort ist ein kurzer Verarbeitungskonflikt aufgetreten. Bitte versuchen Sie es erneut.",
  SERVICE_UNAVAILABLE:
    "⚙️ Der Service ist momentan nicht verfügbar. Bitte kontaktieren Sie den Administrator.",
  UNEXPECTED_ERROR:
    "❌ Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.",
};

type Translate = (key: string, options: { defaultValue: string }) => string;

/**
 * The text a chat message shows: its own, or its notice translated with `t`. Without
 * `t` (a page without i18n) the notice shows in German.
 */
export function messageText(
  message: { text: string; notice?: ChatNotice | null },
  t?: Translate,
): string {
  if (!message.notice) return message.text;
  const fallback = DEFAULT_CHAT_NOTICES[message.notice];
  return t ? t(`chatNotice.${message.notice}`, { defaultValue: fallback }) : fallback;
}
